// define your types here

export interface ApiResponse<T> {
  data: T | null;
  error: string | null; // MSAL server error
}


export interface MenuItem {
  id: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- type is not narrowed here; typing it is a separate change
  Icon: any;
  text: string;
  route: string;
  description: string;
}

export interface runHistory {
  id: number;
  system: string;
  name: string;
  timestamp: string;
  status: string;
}


export interface SyncSchemaData {
  id: number;
  connection_id: number;
}

export interface ConnectionData {
  id: number;
  system_id: number;
  name: string;
}

export interface DetailData {
  description: string;
  status: string;
}

export type RunState = "SCHEDULED" | "RUNNING" | "COMPLETED";
export type ResultStatus = "SUCCESS" | "PARTIAL_SUCCESS" | "FAILED" | "CANCELLED";
export type RunType = "SCHEDULE" | "MANUAL";

export interface RunData {
  id: number;
  connectionId: number;
  syncScopeId: number;
  runDatetime: string;
  state: RunState;
  runType: RunType;
  result: ResultStatus | null;
  error: string | null;
  dryRun: boolean;
  lookbackWeeks: number | null;
  sourceRunId: number | null;
}

export type ProcessedStatus = "SUCCESS" | "FAILED" | "CANCELLED";

export interface ProcessedRegelnummer {
  nummer: number;
  status: ProcessedStatus;
  error: string | null;
}

export interface ProcessedPostnummer {
  nummer: number;
  status: ProcessedStatus;
  error: string | null;
}

export interface LoonModuleResult {
  id: number;
  status: ResultStatus | null;
  error: string | null;
  queryFromChangedAt: string | null;
  processedRegelnummers: ProcessedRegelnummer[];
  retryRunId: number | null;
  retryRunState: RunState | null;
  retryRunResult: ResultStatus | null;
}

export interface InkoopVerkoopModuleResult {
  id: number;
  status: ResultStatus | null;
  error: string | null;
  queryFromChangedAt: string | null;
  processedPostnummers: ProcessedPostnummer[];
  retryRunId: number | null;
  retryRunState: RunState | null;
  retryRunResult: ResultStatus | null;
}

export type StamgegevensAction = "CREATED" | "UPDATED";

export interface StamgegevensChangedField {
  field: string;
  old: string | null;
  new: string | null;
}

export interface StamgegevensItem {
  code: string;
  name: string | null;
  action: StamgegevensAction;
  status: ProcessedStatus;
  error: string | null;
  changedFields: StamgegevensChangedField[] | null;
}

export interface StamgegevensModuleResult {
  id: number;
  module: string;
  status: ResultStatus | null;
  error: string | null;
  items: StamgegevensItem[];
  createdCount: number;
  updatedCount: number;
  failedCount: number;
}

export interface RunDetails {
  id: number;
  runDatetime: string;
  updatedAt: string | null;
  state: RunState;
  runType: RunType;
  result: ResultStatus | null;
  error: string | null;
  syncScopeId: number;
  connectionName: string;
  destinationSystem: string;
  enableSyncDebiteuren: boolean;
  dryRun: boolean;
  lookbackWeeks: number | null;
  sourceRunId: number | null;
  cancelRequestedAt: string | null;
  dryRunConfirmed: boolean;
  loonResult: LoonModuleResult | null;
  inkoopResult: InkoopVerkoopModuleResult | null;
  verkoopResult: InkoopVerkoopModuleResult | null;
  debiteurenResult: StamgegevensModuleResult | null;
  grootboekenResult: StamgegevensModuleResult | null;
  kostenplaatsenResult: StamgegevensModuleResult | null;
}

export interface RetriggerPreviewModule {
  module: string;
  queryFromChangedAt: string;
  querySource: "cursor" | "start_boundary";
}

export interface RetriggerPreview {
  runId: number;
  modules: RetriggerPreviewModule[];
}

/**
 * CONTEXT TYPES
*/

export type MappingType = "journal" | "vatCode";
export type SyncModule = "LOON" | "VERKOOP" | "INKOOP";

export interface ConnectionMapping {
  id?: number;
  connectionId?: number;
  destination: string;
  source: string;
  type: string;  // VAT, Journals, etc.
  module: SyncModule;
}


export enum RunFrequency {
  WEEKLY = "weekly",
  DAILY = "daily",
  MONTHLY = "monthly",
}

export interface synchronisationSchema {
  runFrequency: RunFrequency;
  runDay: number;
  runHour: number;
  runMinute: number;
  lookbackWeeks: number;
  dryRun: boolean;
  isPaused: boolean;
}

export interface SynchronisationSchema {
  id?: number;
  runFrequency: string;  // monthly / weekly / daily
  runDay: number | null;  // For weekly: [0-6] single value, for monthly: [1-31] multiple values, for daily: None
  runHour: number;  // 0-23
  runMinute: number;  // 0-59
  lookbackWeeks: number;
  dryRun: boolean;
  isPaused: boolean;
  nextRunAt?: string | null;
  connectionId?: number;
}

export interface SynchronisationSchemaUpdate {
  runFrequency: RunFrequency;
  runDay: number;
  runHour: number;
  runMinute: number;
  lookbackWeeks: number;
  dryRun: boolean;
  isPaused: boolean;
}

export interface Connection {
  id?: number;
  name: string;
  sourceAdministrationId: number;
  sourceAdministrationName?: string;
  destinationAdministrationId: number;
  destinationSystem?: string | null;
  systemAuthorizationId?: number | null;
  enableSyncDebiteuren: boolean;
  enableSyncGrootboeken: boolean;
  enableSyncKostenplaatsen: boolean;
  synchronisationSchema?: SynchronisationSchema;
  mappings?: ConnectionMapping[];
  missingInitialSyncModules?: string[];
}

export interface SyncLaunchModule {
  module: string;
  lastCursorAt: string | null;
  requiresInitialSyncFrom: boolean;
}

export interface SyncLaunchInfo {
  connectionId: number;
  lookbackWeeks: number;
  modules: SyncLaunchModule[];
}

export interface SyncRunRequest {
  dryRun?: boolean;
  initialSyncFromByModule?: Record<string, string>;
}

export interface SyncRunResponse {
  message: string;
  status: string;
  runId: number;
}

export interface SystemAuthorizationAdministration {
  id: number;
  name: string;
}

export interface SystemAuthorization {
  id: number;
  label: string | null;
  ownerUserId: number | null;
  connectionNames: string[] | null;
  administrations: SystemAuthorizationAdministration[] | null;
  updatedAt: string;
}

export interface NewConnection extends Connection {
  mappings: ConnectionMapping[];
  synchronisationSchema: SynchronisationSchema;
}

export interface SourceAdminstration {
  id: string | number;
  name: string;
}

export interface EasyflexVATCodeEntry {
  ingangsdatum: string | null;
  nederland0: string | null;
  nederlandLaag: string | null;
  nederlandHoog: string | null;
  verlegd: string | null;
  binnenEu: string | null;
  buitenEu: string | null;
}

export interface EasyflexFinancialAdministration {
  administrationCode: number;
  loonjaar: number;
  codeVerkoopboek: string | null;
  codeInkoopboek: string | null;
  codeMemoriaal: string | null;
  codesVerkopen: EasyflexVATCodeEntry[];
  codesInkopen: EasyflexVATCodeEntry[];
  rawRecord: Record<string, unknown>;
}

export interface DestinationAdministration {
  id: number;
  name: string;
}

export interface Destination {
  id: string;
  code: string;
  description: string;
}

export interface VatPercentage {
  Percentage: string //"21.00",
  Type: string // "High"
}

export interface ExactVatCode         {
  Code: string //"NL123456789B01",
  Description: string // "FAKE BTW",
  // type : string // "TEST",
  Percentage: number
  VATTransactionType: string // "S" | "P" | "B"
  Account:string
  division_id: number
  // GLToClaim: string // "123456",
  // GLToPay: string // "0.00",
  // VATPercentages:  VatPercentage[]//[{"Percentage": "21.00", "Type": "High"}],
}

export interface Module {
  division_id: number //3130164,
  Code: string //'300',
  Description: string //'Inkoopboek',
  ID: string //'ffeb9a75-f9ce-4661-85f2-03a7b9d50d18'
  Type: number //22
}

export interface ConnectionCreateInfo {
  availableEasyFlexAdmins: SourceAdminstration[]
  availableDestinations: SourceAdminstration[]
  availableEasyVATCodes: SourceAdminstration[]
  availableDestinationVATCodes: ExactVatCode[]
  availableModules: Module[]
  availableJournals: SourceAdminstration[]
  easyflexFinancialAdmin: EasyflexFinancialAdministration | null
}

export interface User {
  id: number;
  externalId: string;
  email: string;
  tenantId: number;
  administrationCodes: number[];
  module: string;
}

export interface EasyflexAdministration {
  code: number;
  name: string;
  hasApiKey: boolean;
  isSandbox: boolean;
  sandboxAllowed: boolean;
}

// DataStudio Types
export interface EndpointInfo {
  id: string;
  label: string;
  module: string;
  description?: string;
}

export interface Parameter {
  id: string;
  type: string;
  required: string; // C, O, or V
  description: string;
  details?: string | null;
  options?: EndpointValueOption[];
}

export interface EndpointValueOption {
  value: string;
  label: string;
}

export interface Field {
  id: string;
  type: string;
  description: string;
  details?: string | null;
  options?: EndpointValueOption[];
  children?: Field[];
}

export interface EndpointDefinition {
  id: string;
  label: string;
  module: string;
  description: string;
  parameters: Parameter[];
  fields: Field[];
}

export interface DataStudioQuery {
  module: string;
  parameters: Record<string, unknown>;
  fields: string[];
  administration_code: number;
  source_name: string;
  idempotency_key: string;
}

export interface DataStudioTaskResponse {
  task_id: string;
  kind: "FETCH" | "EXPORT";
  state: "QUEUED" | "RUNNING" | "COMPLETED" | "FAILED" | "CANCELLED";
  administration_code: number;
  source_name?: string | null;
  row_count: number | null;
  preview: Record<string, unknown>[];
  library_item_id: string | null;
  error_code: string | null;
  error_message: string | null;
}

export interface DataChatMessage {
  id?: string;
  dbId?: string;
  role: "user" | "assistant";
  type: "text" | "code_logs" | "component" | "image";
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- type is not narrowed here; typing it is a separate change
  content: any; // string for text, object for component
  timestamp?: number;
}

export interface DataSource {
  endpoint_name: string;
  source_name?: string;
  selected_fields: string[];
  parameters?: Record<string, unknown>;
  task_id?: string;
  library_item_id?: string;
  row_count?: number | null;
  state?: DataStudioTaskResponse["state"];
  error_message?: string | null;
}

export interface DataChatRequest {
  message: string;
  data_sources?: DataSource[];
  session_id?: string;
  history?: DataChatMessage[];
  administration_code: number;
}

export interface DataChatResponse {
  session_id?: string;
  messages: DataChatMessage[];
  stored_data_keys?: string[];
  stored_data_previews?: Record<string, Record<string, unknown>[]>;
  available_data_sources?: string[];
}

export interface DryRunModuleData {
  module: string;
  recordCount: number;
  data: Record<string, unknown>[];
  cursorDate: string | null;
  journaalpostCount: number | null;
}

export interface DryRunResponse {
  runId: number;
  runDatetime: string;
  connectionName: string;
  state: RunState;
  modules: DryRunModuleData[];
}

export interface DataStudioApiKey {
  id: string;
  name: string;
  administrationCode: number;
  createdAt: string;
  expiresAt: string;
  revokedAt: string | null;
}

export interface DataStudioApiKeyCreated extends DataStudioApiKey {
  token: string;
}
